const LEGACY_ABOUT_SECTION_SELECTOR = 'section.about, section.prerequisites, section.course-staff, section.faq';

type ProcessOverviewContentOptions = {
  getFallbackStaffName?: (index: number) => string;
};

const rewriteRelativeAssetPaths = (markup: string, lmsBaseUrl: string): string => markup.replace(
  /src="\/(static\/images\/|asset)/g,
  (_, path) => `src="${lmsBaseUrl}/${path}`,
);

const getCollapsedText = (value?: string | null) => value?.replace(/\s+/g, ' ').trim() || '';

const createElement = <K extends keyof HTMLElementTagNameMap>(
  documentRef: Document,
  tagName: K,
  className?: string,
  textContent?: string,
) => {
  const element = documentRef.createElement(tagName);

  if (className) {
    element.className = className;
  }

  if (textContent) {
    element.textContent = textContent;
  }

  return element;
};

const buildStaffCard = (
  documentRef: Document,
  teacherNode: Element,
  index: number,
  options: ProcessOverviewContentOptions,
) => {
  const staffCard = createElement(documentRef, 'article', 'catalog-course-about-overview__staff-card');
  const media = createElement(documentRef, 'div', 'catalog-course-about-overview__staff-media');
  const content = createElement(documentRef, 'div', 'catalog-course-about-overview__staff-content');

  const image = teacherNode.querySelector('img');
  const headingNode = teacherNode.querySelector('h1, h2, h3, h4, h5, h6, strong, b');
  const paragraphText = Array.from(teacherNode.querySelectorAll('p'))
    .map((paragraph) => getCollapsedText(paragraph.textContent))
    .filter(Boolean);

  const fallbackText = getCollapsedText(teacherNode.textContent);
  const staffName = getCollapsedText(headingNode?.textContent)
    || fallbackText
    || options.getFallbackStaffName?.(index)
    || String(index + 1);
  const bioFromFallback = fallbackText && fallbackText !== staffName ? fallbackText.replace(staffName, '').trim() : '';
  const staffBio = paragraphText.join(' ') || bioFromFallback;

  if (image) {
    const imageClone = image.cloneNode(true) as HTMLImageElement;
    imageClone.removeAttribute('width');
    imageClone.removeAttribute('height');
    media.appendChild(imageClone);
  } else {
    const avatar = createElement(
      documentRef,
      'span',
      'catalog-course-about-overview__staff-avatar',
      staffName.charAt(0).toUpperCase(),
    );
    media.appendChild(avatar);
  }

  content.appendChild(createElement(documentRef, 'h3', 'catalog-course-about-overview__staff-name', staffName));

  if (staffBio) {
    content.appendChild(createElement(documentRef, 'p', 'catalog-course-about-overview__staff-bio', staffBio));
  }

  staffCard.appendChild(media);
  staffCard.appendChild(content);

  return staffCard;
};

const transformCourseStaffSection = (
  documentRef: Document,
  section: Element,
  options: ProcessOverviewContentOptions,
) => {
  const teacherNodes = Array.from(section.querySelectorAll(':scope > article.teacher, :scope > .teacher'));

  if (!teacherNodes.length) {
    return;
  }

  const staffGrid = createElement(documentRef, 'div', 'catalog-course-about-overview__staff-grid');

  teacherNodes.forEach((teacherNode, index) => {
    staffGrid.appendChild(buildStaffCard(documentRef, teacherNode, index, options));
    teacherNode.remove();
  });

  section.appendChild(staffGrid);
};

const transformFaqSection = (documentRef: Document, section: Element) => {
  const responsesContainer = section.querySelector('.responses');

  if (!responsesContainer) {
    return;
  }

  responsesContainer.classList.add('catalog-course-about-overview__faq-list');

  const faqItems = Array.from(responsesContainer.querySelectorAll(':scope > article.response'));
  faqItems.forEach((faqItem) => faqItem.classList.add('catalog-course-about-overview__faq-item'));

  const looseNodes = Array.from(responsesContainer.childNodes).filter((node) => {
    if (node.nodeType === 3) {
      return getCollapsedText(node.textContent);
    }

    if (node.nodeType === 1) {
      const element = node as Element;
      return !element.matches('h1, h2, h3, h4, h5, h6, article.response');
    }

    return false;
  });

  if (!looseNodes.length) {
    return;
  }

  const footnote = createElement(documentRef, 'div', 'catalog-course-about-overview__faq-footnote');
  looseNodes.forEach((node) => {
    footnote.appendChild(node.cloneNode(true));
    node.parentNode?.removeChild(node);
  });

  responsesContainer.appendChild(footnote);
};

/**
 * Processes overview content to normalize legacy course-about HTML and replace image paths.
 * @param overview - The overview HTML content
 * @returns Processed overview content with updated image paths
 */
export const processOverviewContent = (
  overview: string,
  lmsBaseUrl: string,
  options: ProcessOverviewContentOptions = {},
): string => {
  if (!overview) {
    return overview;
  }

  const rewrittenOverview = rewriteRelativeAssetPaths(overview, lmsBaseUrl);

  if (typeof DOMParser === 'undefined') {
    return rewrittenOverview;
  }

  const parser = new DOMParser();
  const documentRef = parser.parseFromString(rewrittenOverview, 'text/html');
  const rootElement = documentRef.body.firstElementChild;

  if (!rootElement) {
    return rewrittenOverview;
  }

  rootElement.classList.add('catalog-course-about-overview__legacy-root');

  Array.from(documentRef.querySelectorAll('[src]')).forEach((element) => {
    const src = element.getAttribute('src');

    if (src?.startsWith('/static/images/') || src?.startsWith('/asset')) {
      element.setAttribute('src', `${lmsBaseUrl}${src}`);
    }
  });

  Array.from(documentRef.querySelectorAll(LEGACY_ABOUT_SECTION_SELECTOR)).forEach((section) => {
    section.classList.add('catalog-course-about-overview__section');
  });

  Array.from(documentRef.querySelectorAll('section.course-staff')).forEach((section) => {
    section.classList.add('catalog-course-about-overview__section--staff');
    transformCourseStaffSection(documentRef, section, options);
  });

  Array.from(documentRef.querySelectorAll('section.faq')).forEach((section) => {
    section.classList.add('catalog-course-about-overview__section--faq');
    transformFaqSection(documentRef, section);
  });

  return documentRef.body.innerHTML;
};
